import type { GpuContext } from '../context.js';
import { workgroupCount } from '../context.js';
import type { GpuImage } from '../resources.js';
import { Struct } from '../uniform-writer.js';
import { FILTER_AREA } from '../types.js';
import { boxBlur } from './blur.js';
import { resampleTo } from './resample.js';

export interface GuidedFilterOptions {
  /** Window radius in full-resolution pixels. */
  radius?: number;
  /** Regularisation; smaller hugs edges harder. */
  eps?: number;
  /**
   * Solve the linear model at 1/subsample resolution (He et al.'s fast guided
   * filter). 4 is a good default: quality is visually identical and the cost
   * drops ~16x, which is what keeps the refinement sliders interactive.
   */
  subsample?: number;
  /** Compute guide luminance in linear light rather than sRGB. */
  linearGuide?: boolean;
}

/**
 * Edge-aware refinement of a matte, guided by the source image.
 *
 * This is the single highest-impact step for POD artwork: the network matte is
 * produced at 1024x1024 and upsampled, so hand-lettering, thin outlines and
 * halftone dots arrive soft and slightly misaligned. The guided filter snaps the
 * alpha back onto the image's own gradients.
 */
export function guidedFilter(
  ctx: GpuContext,
  guide: GpuImage,
  alpha: GpuImage,
  dst: GpuImage,
  options: GuidedFilterOptions = {},
): void {
  const radius = Math.max(1, Math.round(options.radius ?? 8));
  const eps = options.eps ?? 1e-4;
  const subsample = Math.max(1, Math.round(options.subsample ?? 4));
  const linear = options.linearGuide ? 1 : 0;

  const smallWidth = Math.max(8, Math.ceil(guide.width / subsample));
  const smallHeight = Math.max(8, Math.ceil(guide.height / subsample));
  const smallRadius = Math.max(1, Math.round(radius / subsample));

  const guideSmall =
    subsample === 1
      ? guide
      : resampleTo(ctx, guide, smallWidth, smallHeight, { filter: FILTER_AREA });
  const alphaSmall =
    subsample === 1
      ? alpha
      : resampleTo(ctx, alpha, smallWidth, smallHeight, { filter: FILTER_AREA });

  const moments = ctx.image(guideSmall.width, guideSmall.height, 'rgba32float', 'guided-moments');
  ctx.dispatch(
    {
      module: 'guided_moments.wgsl',
      bindings: [
        { kind: 'texture', format: guideSmall.format },
        { kind: 'texture', format: alphaSmall.format },
        { kind: 'storageTexture', format: 'rgba32float' },
        { kind: 'uniform' },
      ],
    },
    [
      guideSmall,
      alphaSmall,
      moments,
      ctx.uniform(new Struct(16).i(0, guideSmall.width, guideSmall.height).u(8, linear, 0).bytes),
    ],
    [workgroupCount(moments.width, 8), workgroupCount(moments.height, 8)],
    'guided:moments',
  );

  boxBlur(ctx, moments, moments, smallRadius);

  const coeff = ctx.image(moments.width, moments.height, 'rg32float', 'guided-coeff');
  ctx.dispatch(
    {
      module: 'guided_coeff.wgsl',
      bindings: [
        { kind: 'texture', format: 'rgba32float' },
        { kind: 'storageTexture', format: 'rg32float' },
        { kind: 'uniform' },
      ],
    },
    [
      moments,
      coeff,
      ctx.uniform(new Struct(16).i(0, moments.width, moments.height).f(8, eps, 0).bytes),
    ],
    [workgroupCount(coeff.width, 8), workgroupCount(coeff.height, 8)],
    'guided:coeff',
  );

  boxBlur(ctx, coeff, coeff, smallRadius);

  ctx.dispatch(
    {
      module: 'guided_apply.wgsl',
      bindings: [
        { kind: 'texture', format: guide.format },
        { kind: 'texture', format: 'rg32float' },
        { kind: 'storageTexture', format: 'r32float' },
        { kind: 'uniform' },
      ],
    },
    [
      guide,
      coeff,
      dst,
      ctx.uniform(
        new Struct(32)
          .i(0, dst.width, dst.height)
          .i(8, coeff.width, coeff.height)
          .f(16, coeff.width / dst.width, coeff.height / dst.height)
          .u(24, linear, 1).bytes,
      ),
    ],
    [workgroupCount(dst.width, 8), workgroupCount(dst.height, 8)],
    'guided:apply',
  );

  moments.release();
  coeff.release();
  if (guideSmall !== guide) guideSmall.release();
  if (alphaSmall !== alpha) alphaSmall.release();
}
